import React, {memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {flexRender, getCoreRowModel, useReactTable, type ColumnDef} from '@tanstack/react-table'
import {type TranslationObject} from '@colonist/i18n'
import {T} from '@colonist/ui'
import styles from './Table.module.scss'
import clsx from 'clsx'

declare module '@tanstack/react-table' {
    interface ColumnMeta<TData, TValue> {
        align?: 'left' | 'center' | 'right'
        width?: string
    }
}

export interface StickyRow<TItem> {
    index: number
    data: TItem
}

export interface TableProps<TItem> {
    data: TItem[]
    columns: ColumnDef<TItem>[]
    onRowClick?: (row: TItem) => void
    fetchMoreOnReachBottom?: VoidFunction
    stickyRow?: StickyRow<TItem>
    emptyText?: TranslationObject
    backgroundStyle?: 'filled' | 'striped'
    className?: string
}

function TableComponent<TItem>(props: TableProps<TItem>) {
    const {
        data,
        columns,
        onRowClick,
        fetchMoreOnReachBottom,
        stickyRow,
        emptyText,
        backgroundStyle = 'filled',
        className,
    } = props
    const containerRef = useRef<HTMLDivElement>(null)
    const theadRef = useRef<HTMLTableSectionElement>(null)
    const fetchMoreCalledRef = useRef(false)
    const [headerHeight, setHeaderHeight] = useState(0)

    useLayoutEffect(() => {
        if(theadRef.current) {
            setHeaderHeight(theadRef.current.getBoundingClientRect().height)
        }
    }, [])

    const hasNoData = data.length == 0
    const hasStickyRow = stickyRow != undefined
    const stickyRowInData = hasStickyRow && stickyRow.index < data.length
    const shouldAppendStickyRowToBottom = hasStickyRow && !stickyRowInData

    const tableData = useMemo(() => {
        if(shouldAppendStickyRowToBottom) {
            return [...data, stickyRow.data]
        }
        return data
    }, [data, stickyRow, shouldAppendStickyRowToBottom])

    const table = useReactTable({
        data: tableData,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
    })

    useEffect(() => {
        fetchMoreCalledRef.current = false
    }, [data])

    const handleScroll = useCallback(() => {
        if(!fetchMoreOnReachBottom) return
        if(fetchMoreCalledRef.current) return
        const containerElement = containerRef.current
        if(!containerElement) return

        const distanceFromBottom = containerElement.scrollHeight - containerElement.scrollTop - containerElement.clientHeight
        const fetchMoreThresholdPx = 200
        if(distanceFromBottom < fetchMoreThresholdPx) {
            fetchMoreCalledRef.current = true
            fetchMoreOnReachBottom()
        }
    }, [fetchMoreOnReachBottom])

    useEffect(() => {
        const containerElement = containerRef.current
        if(!containerElement) return

        containerElement.addEventListener('scroll', handleScroll, {passive: true})
        return () => containerElement.removeEventListener('scroll', handleScroll)
    }, [handleScroll])

    return (
        <div ref={containerRef} className={clsx(styles.container, className)}>
            <table className={clsx(styles.table, styles[backgroundStyle])}>
                <thead ref={theadRef} className={styles.tableHeader}>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    className={styles.tableHeaderCell}
                                    style={{
                                        textAlign: header.column.columnDef.meta?.align ?? 'left',
                                        width: header.column.columnDef.meta?.width,
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {hasNoData && emptyText ? (
                        <tr>
                            <td colSpan={columns.length} className={styles.emptyContainer}>
                                <T id={emptyText} />
                            </td>
                        </tr>
                    ) : (
                        table.getRowModel().rows.map((row) => {
                            const isSticky = hasStickyRow && (
                                (stickyRowInData && row.index == stickyRow.index) ||
                                (shouldAppendStickyRowToBottom && row.index == tableData.length - 1)
                            )
                            return (
                                <tr
                                    key={row.id}
                                    className={clsx(styles.tableRow, {
                                        [styles.clickable]: onRowClick,
                                        [styles.stickyRow]: isSticky,
                                    })}
                                    style={isSticky ? {top: headerHeight, bottom: 0} : undefined}
                                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            key={cell.id}
                                            className={clsx(styles.tableCell, {
                                                [styles.stickyCell]: isSticky,
                                            })}
                                            style={{textAlign: cell.column.columnDef.meta?.align ?? 'left'}}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                            )
                        })
                    )}
                </tbody>
            </table>
        </div>
    )
}

export const Table = memo(TableComponent) as typeof TableComponent
